import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetch tasks with filters. Polls every 30s.
 */
export function useTasks(filters = {}) {
    const { getAuthHeader } = useAuth();
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.contact_id) params.set('contact_id', filters.contact_id);
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    if (filters.sort_dir) params.set('sort_dir', filters.sort_dir);

    const qs = params.toString();
    return useQuery({
        queryKey: ['tasks', qs],
        queryFn: async () => {
            const data = await apiFetch(`/activities/tasks${qs ? `?${qs}` : ''}`, getAuthHeader());
            return Array.isArray(data) ? data : [];
        },
        staleTime: 30 * 1000,
        refetchInterval: 30 * 1000,
    });
}

/**
 * Task count summary for sidebar badge + page header.
 */
export function useTasksSummary() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['tasksSummary'],
        queryFn: async () => {
            try {
                return await apiFetch('/activities/tasks/summary', getAuthHeader());
            } catch {
                return { overdue: 0, today: 0, upcoming: 0, completed_today: 0, total_open: 0 };
            }
        },
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Create a new task.
 */
export function useCreateTask() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (task) => {
            const res = await axios.post(`${API}/activities`, {
                type: 'task',
                status: 'planned',
                ...task,
            }, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['tasksSummary'] });
        },
    });
}

/**
 * Quick-complete a task.
 */
export function useCompleteTask() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (activityId) => {
            const res = await axios.put(`${API}/activities/${activityId}/complete`, {}, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['tasksSummary'] });
        },
    });
}

/**
 * Reopen a completed task.
 */
export function useReopenTask() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (activityId) => {
            const res = await axios.put(`${API}/activities/${activityId}/reopen`, {}, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['tasksSummary'] });
        },
    });
}

/**
 * Bulk complete tasks.
 */
export function useBulkCompleteTasks() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (activityIds) => {
            const res = await axios.post(`${API}/activities/bulk-complete`, { activity_ids: activityIds }, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['tasksSummary'] });
        },
    });
}

/**
 * Bulk delete tasks.
 */
export function useBulkDeleteTasks() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (activityIds) => {
            const res = await axios.post(`${API}/activities/bulk-delete`, { activity_ids: activityIds }, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['tasksSummary'] });
        },
    });
}

/**
 * Update a task.
 */
export function useUpdateTask() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ activityId, updates }) => {
            const res = await axios.put(`${API}/activities/${activityId}`, updates, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['tasksSummary'] });
        },
    });
}

/**
 * AI task suggestions.
 */
export function useSuggestTasks() {
    const { getAuthHeader } = useAuth();
    return useMutation({
        mutationFn: async ({ context_type, context_id } = {}) => {
            const body = {};
            if (context_type) body.context_type = context_type;
            if (context_id) body.context_id = context_id;
            const res = await axios.post(`${API}/ai/suggest-tasks`, body, { headers: getAuthHeader() });
            return res.data.suggestions || [];
        },
    });
}

/**
 * Delete a task.
 */
export function useDeleteTask() {
    const { getAuthHeader } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (activityId) => {
            const res = await axios.delete(`${API}/activities/${activityId}`, { headers: getAuthHeader() });
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['tasksSummary'] });
        },
    });
}
